import { Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Layout } from "@/app/Layout";
import { TopNav } from "@/app/TopNav";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FloatingActionButton } from "@/components/shared/utility/FloatingActionButton";
import { DocumentCreateLauncherProvider } from "@/features/document-create/components/DocumentCreateLauncherProvider";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/components/ui/utils";
import { AuthGuard, LoginPage } from "@/features/auth";
import { NotificationsProvider } from "@/features/notifications/notifications-context";
import { VoiceAssistantLauncher } from "@/features/voice-agent/components/VoiceAssistantLauncher";
import { VoiceAssistantProvider } from "@/features/voice-agent/VoiceAssistantContext";
import { isAssistantPathname } from "@/features/voice-agent/assistant-route";
import { useAppUpdate } from "@/hooks/use-app-update";
import { lazyRoute } from "@/lib/lazy-route";

const SharedDocumentPage = lazyRoute(
  async () => (await import("@/features/portal/pages/SharedDocumentPage")).default,
);
const Dashboard = lazyRoute(
  async () => (await import("@/features/dashboard/pages/DashboardPage")).Dashboard,
);
const Accounts = lazyRoute(
  async () => (await import("@/features/accounts/pages/AccountsPage")).Accounts,
);
const AccountDetailPage = lazyRoute(
  async () => (await import("@/features/accounts/pages/AccountDetailPage")).AccountDetailPage,
);
const InvoicesUnifiedPage = lazyRoute(
  async () => (await import("@/features/invoices/pages/InvoicesUnifiedPage")).InvoicesUnifiedPage,
);
const InvoiceDetailPage = lazyRoute(
  async () => (await import("@/features/invoices/pages/InvoiceDetailPage")).InvoiceDetailPage,
);
const InvoiceEditPage = lazyRoute(
  async () => (await import("@/features/invoices/pages/InvoiceEditPage")).InvoiceEditPage,
);
const EstimatesUnifiedPage = lazyRoute(
  async () =>
    (await import("@/features/estimates/pages/EstimatesUnifiedPage")).EstimatesUnifiedPage,
);
const EstimateDetailPage = lazyRoute(
  async () => (await import("@/features/estimates/pages/EstimateDetailPage")).EstimateDetailPage,
);
const EstimateEditPage = lazyRoute(
  async () => (await import("@/features/estimates/pages/EstimateEditPage")).EstimateEditPage,
);
const PurchaseOrdersUnifiedPage = lazyRoute(
  async () =>
    (await import("@/features/purchase-orders/pages/PurchaseOrdersUnifiedPage"))
      .PurchaseOrdersUnifiedPage,
);
const PurchaseOrderDetailPage = lazyRoute(
  async () =>
    (await import("@/features/purchase-orders/pages/PurchaseOrderDetailPage"))
      .PurchaseOrderDetailPage,
);
const PurchaseOrderEditPage = lazyRoute(
  async () =>
    (await import("@/features/purchase-orders/pages/PurchaseOrderEditPage")).PurchaseOrderEditPage,
);
const POManagementKanbanPage = lazyRoute(
  async () =>
    (await import("@/features/purchase-orders/board/pages/POManagementKanbanPage"))
      .POManagementKanbanPage,
);
const AnalyticsDashboard = lazyRoute(
  async () =>
    (await import("@/features/analytics/pages/AnalyticsDashboardPage")).AnalyticsDashboard,
);
const Accounting = lazyRoute(
  async () => (await import("@/features/accounting/pages/AccountingPage")).Accounting,
);
const FinanceOverview = lazyRoute(
  async () => (await import("@/features/finance/pages/FinanceDashboardPage")).FinanceDashboardPage,
);
const PortalPaymentsManagementPage = lazyRoute(
  async () => (await import("@/app/PortalPaymentsManagementPage")).PortalPaymentsManagementPage,
);
const CustomerCreditsPage = lazyRoute(
  async () => (await import("@/features/credits/pages/CustomerCreditsPage")).CustomerCreditsPage,
);
const VendorCreditsPage = lazyRoute(
  async () => (await import("@/features/vendor-credits/pages/VendorCreditsPage")).VendorCreditsPage,
);
const CustomerPaymentsPage = lazyRoute(
  async () =>
    (await import("@/features/customer-payments/pages/CustomerPaymentsPage")).CustomerPaymentsPage,
);
const CustomerPaymentDetailPage = lazyRoute(
  async () =>
    (await import("@/features/customer-payments/pages/CustomerPaymentDetailPage"))
      .CustomerPaymentDetailPage,
);
const CustomerPaymentEditPage = lazyRoute(
  async () =>
    (await import("@/features/customer-payments/pages/CustomerPaymentEditPage"))
      .CustomerPaymentEditPage,
);
const VendorPaymentsPage = lazyRoute(
  async () =>
    (await import("@/features/vendor-payments/pages/VendorPaymentsPage")).VendorPaymentsPage,
);
const VendorPaymentDetailPage = lazyRoute(
  async () =>
    (await import("@/features/vendor-payments/pages/VendorPaymentDetailPage"))
      .VendorPaymentDetailPage,
);
const VendorPaymentEditPage = lazyRoute(
  async () =>
    (await import("@/features/vendor-payments/pages/VendorPaymentEditPage")).VendorPaymentEditPage,
);
const ExpenseDashboard = lazyRoute(
  async () => (await import("@/features/expenses/pages/ExpenseDashboard")).ExpenseDashboard,
);
const ExpensesPage = lazyRoute(
  async () => (await import("@/features/expenses/pages/ExpensesPage")).ExpensesPage,
);
const CreateExpensePage = lazyRoute(
  async () => (await import("@/features/expenses/pages/CreateExpensePage")).CreateExpensePage,
);
const ExpenseDetailPage = lazyRoute(
  async () => (await import("@/features/expenses/pages/ExpenseDetailPage")).ExpenseDetailPage,
);
const EditExpensePage = lazyRoute(
  async () => (await import("@/features/expenses/pages/EditExpensePage")).EditExpensePage,
);
const RecurringExpensesPage = lazyRoute(
  async () =>
    (await import("@/features/expenses/pages/RecurringExpensesPage")).RecurringExpensesPage,
);
const ExpenseCategoriesPage = lazyRoute(
  async () =>
    (await import("@/features/expenses/pages/ExpenseCategoriesPage")).ExpenseCategoriesPage,
);
const Inventory = lazyRoute(
  async () => (await import("@/features/inventory/pages/InventoryPage")).Inventory,
);
const ProductDetailPage = lazyRoute(
  async () => (await import("@/features/inventory/pages/ProductDetailPage")).ProductDetailPage,
);
const ProductMatchingPage = lazyRoute(
  async () =>
    (await import("@/features/product-matching/pages/ProductMatchingPage")).ProductMatchingPage,
);
const InventoryManagementPage = lazyRoute(
  async () =>
    (await import("@/features/inventory/management/pages/InventoryManagementPage"))
      .InventoryManagementPage,
);
const Operations = lazyRoute(
  async () => (await import("@/features/operations/pages/OperationsPage")).Operations,
);
const ShippingDetailPage = lazyRoute(
  async () => (await import("@/features/operations/pages/ShippingDetailPage")).ShippingDetailPage,
);
const ShippingCalendarPage = lazyRoute(
  async () =>
    (await import("@/features/operations/pages/ShippingCalendarPage")).ShippingCalendarPage,
);
const ProjectsPage = lazyRoute(
  async () => (await import("@/features/projects/pages/ProjectsPage")).ProjectsPage,
);
const ProjectDetailPage = lazyRoute(
  async () => (await import("@/features/projects/pages/ProjectDetailPage")).ProjectDetailPage,
);
const TasksPage = lazyRoute(
  async () => (await import("@/features/tasks/pages/TasksPage")).TasksPage,
);
const TaskSearchPage = lazyRoute(
  async () => (await import("@/features/tasks/pages/TaskSearchPage")).TaskSearchPage,
);
const TaskCalendarPage = lazyRoute(
  async () => (await import("@/features/tasks/pages/TaskCalendarPage")).TaskCalendarPage,
);
const TaskActivityPage = lazyRoute(
  async () => (await import("@/features/tasks/pages/TaskActivityPage")).TaskActivityPage,
);
const SyncDashboardPage = lazyRoute(
  async () => (await import("@/features/sync/pages/SyncDashboardPage")).SyncDashboardPage,
);
const SyncCleanupPage = lazyRoute(
  async () => (await import("@/features/sync/pages/SyncCleanupPage")).SyncCleanupPage,
);
const DeferredErrorsPage = lazyRoute(
  async () => (await import("@/features/sync/pages/DeferredErrorsPage")).DeferredErrorsPage,
);
const Settings = lazyRoute(
  async () => (await import("@/features/settings/pages/SettingsPage")).Settings,
);
const DeletionArchivePage = lazyRoute(
  async () => (await import("@/features/admin/pages/DeletionArchivePage")).DeletionArchivePage,
);
const OperatorsPage = lazyRoute(
  async () => (await import("@/features/admin/pages/OperatorsPage")).OperatorsPage,
);
const ImportExportPage = lazyRoute(
  async () => (await import("@/features/import-export")).ImportExportPage,
);
const ThemeShowcasePage = lazyRoute(
  async () => (await import("@/features/dev/pages/ThemeShowcasePage")).ThemeShowcasePage,
);
const NotificationsPage = lazyRoute(
  async () => (await import("@/features/notifications/pages/NotificationsPage")).NotificationsPage,
);
const VoiceAgentPage = lazyRoute(
  async () => (await import("@/features/voice-agent/pages/VoiceAgentPage")).VoiceAgentPage,
);

function RoutePendingState({ fullScreen = false }: { fullScreen?: boolean }) {
  return (
    <output
      aria-live="polite"
      className={`flex items-center justify-center ${fullScreen ? "min-h-dvh" : "min-h-[50vh]"}`}
    >
      <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none" />
      <span className="sr-only">Loading page</span>
    </output>
  );
}

function ProtectedShell() {
  const showLaunchers = !isAssistantPathname(useLocation().pathname);
  return (
    <AuthGuard>
      <NotificationsProvider>
        <VoiceAssistantProvider>
          <SidebarProvider>
            <Layout />
            {/* The bottom padding keeps the last row's actions clear of the floating launchers. */}
            <SidebarInset className={cn("min-w-0", showLaunchers && "pb-(--launcher-clearance)")}>
              <TopNav />
              <ErrorBoundary>
                <DocumentCreateLauncherProvider>
                  <Suspense fallback={<RoutePendingState />}>
                    <Outlet />
                  </Suspense>
                  {showLaunchers && <FloatingActionButton />}
                </DocumentCreateLauncherProvider>
              </ErrorBoundary>
              <VoiceAssistantLauncher />
            </SidebarInset>
          </SidebarProvider>
        </VoiceAssistantProvider>
      </NotificationsProvider>
    </AuthGuard>
  );
}

const protectedShellRoutes = (
  <Route element={<ProtectedShell />}>
    <Route element={<Dashboard />} path="/" />
    <Route element={<Accounts />} path="/accounts" />
    <Route element={<AccountDetailPage />} path="/accounts/:id" />
    <Route element={<InvoicesUnifiedPage />} path="/invoices" />
    <Route element={<InvoicesUnifiedPage />} path="/invoices/create" />
    <Route element={<InvoicesUnifiedPage />} path="/invoices/payments" />
    <Route element={<Navigate replace to="../create" />} path="/invoices/new" />
    <Route element={<InvoiceDetailPage />} path="/invoices/:id" />
    <Route element={<InvoiceEditPage />} path="/invoices/:id/edit" />
    <Route element={<EstimatesUnifiedPage />} path="/estimates" />
    <Route element={<EstimatesUnifiedPage />} path="/estimates/create" />
    <Route element={<EstimatesUnifiedPage />} path="/estimates/related" />
    <Route element={<Navigate replace to="../create" />} path="/estimates/new" />
    <Route element={<EstimateDetailPage />} path="/estimates/:id" />
    <Route element={<EstimateEditPage />} path="/estimates/:id/edit" />
    <Route element={<PurchaseOrdersUnifiedPage />} path="/purchase-orders" />
    <Route element={<PurchaseOrdersUnifiedPage />} path="/purchase-orders/create" />
    <Route element={<PurchaseOrdersUnifiedPage />} path="/purchase-orders/payments" />
    <Route element={<Navigate replace to="../create" />} path="/purchase-orders/new" />
    <Route element={<POManagementKanbanPage />} path="/purchase-orders/kanban" />
    <Route element={<PurchaseOrderDetailPage />} path="/purchase-orders/:id" />
    <Route element={<PurchaseOrderEditPage />} path="/purchase-orders/:id/edit" />
    <Route element={<AnalyticsDashboard />} path="/analytics" />
    <Route element={<Accounting />} path="/accounting" />
    <Route element={<FinanceOverview />} path="/finance" />
    <Route element={<PortalPaymentsManagementPage />} path="/portal-payments" />
    <Route element={<CustomerCreditsPage />} path="/credits" />
    <Route element={<VendorCreditsPage />} path="/vendor-credits" />
    <Route element={<CustomerPaymentsPage />} path="/customer-payments" />
    <Route element={<CustomerPaymentDetailPage />} path="/customer-payments/:id" />
    <Route element={<CustomerPaymentEditPage />} path="/customer-payments/:id/edit" />
    <Route element={<VendorPaymentsPage />} path="/vendor-payments" />
    <Route element={<VendorPaymentDetailPage />} path="/vendor-payments/:id" />
    <Route element={<VendorPaymentEditPage />} path="/vendor-payments/:id/edit" />
    <Route element={<ExpenseDashboard />} path="/expenses" />
    <Route element={<ExpensesPage />} path="/expenses/list" />
    <Route element={<CreateExpensePage />} path="/expenses/create" />
    <Route element={<ExpenseDetailPage />} path="/expenses/:id" />
    <Route element={<EditExpensePage />} path="/expenses/:id/edit" />
    <Route element={<RecurringExpensesPage />} path="/expenses/recurring" />
    <Route element={<ExpenseCategoriesPage />} path="/expense-categories" />
    <Route element={<Inventory />} path="/inventory" />
    <Route element={<ProductDetailPage />} path="/inventory/:id" />
    <Route element={<InventoryManagementPage />} path="/inventory-management" />
    <Route element={<ProductMatchingPage />} path="/product-matching" />
    <Route element={<Operations />} path="/operations" />
    <Route element={<Navigate replace to="/operations" />} path="/operations/submit-tp" />
    <Route element={<ShippingCalendarPage />} path="/operations/calendar" />
    <Route element={<ShippingDetailPage />} path="/operations/:id" />
    <Route element={<ProjectsPage />} path="/projects" />
    <Route element={<ProjectDetailPage />} path="/projects/:id" />
    <Route element={<TasksPage />} path="/tasks" />
    <Route element={<TaskSearchPage />} path="/tasks/search" />
    <Route element={<TaskCalendarPage />} path="/tasks/calendar" />
    <Route element={<TaskActivityPage />} path="/tasks/activity" />
    <Route element={<SyncDashboardPage />} path="/sync" />
    <Route element={<SyncCleanupPage />} path="/sync/cleanup" />
    <Route element={<DeferredErrorsPage />} path="/sync/errors" />
    <Route element={<Settings />} path="/settings" />
    <Route element={<NotificationsPage />} path="/notifications" />
    <Route element={<VoiceAgentPage />} path="/assistant" />
    <Route element={<Navigate replace to="/assistant" />} path="/voice" />
    <Route element={<DeletionArchivePage />} path="/admin/deletion-archive" />
    <Route element={<OperatorsPage />} path="/admin/operators" />
    <Route element={<ImportExportPage />} path="/admin/import-export" />
    <Route element={<Navigate replace to="/" />} path="*" />
  </Route>
);

/** Shared-document links are the customer surface; they never install the worker. */
const CUSTOMER_LINK_PREFIX = "/document-view/";

/** Registers the service worker and drives its update prompt. */
function AppUpdate() {
  useAppUpdate();
  return null;
}

/** Defines the public SPA route tree, shared shell, and global toast outlet. */
export default function App() {
  const { pathname } = useLocation();
  // Mounted here, beside the Toaster that renders its prompt, for staff routes
  // and sign-in. A customer opening a shared document would otherwise download
  // the whole precache for an app they never use; a browser that already has
  // the worker from staff use stays controlled through it either way.
  // React Router matches paths case-insensitively, so the check does too.
  const registersWorker = !pathname.toLowerCase().startsWith(CUSTOMER_LINK_PREFIX);
  return (
    <>
      {registersWorker ? <AppUpdate /> : null}
      <Routes>
        <Route element={<LoginPage key="login" />} path="/login" />
        <Route element={<LoginPage key="reset-password" />} path="/reset-password" />
        <Route
          element={
            <Suspense fallback={<RoutePendingState fullScreen />}>
              <SharedDocumentPage />
            </Suspense>
          }
          path="/document-view/:token"
        />
        <Route
          element={
            <Suspense fallback={<RoutePendingState fullScreen />}>
              <ThemeShowcasePage />
            </Suspense>
          }
          path="/theme"
        />
        {protectedShellRoutes}
      </Routes>
      <Toaster
        closeButton
        mobileOffset={{
          // Up to 600px Sonner sizes the toast from the left offset on both sides.
          left: "calc(16px + max(var(--shell-inset-left), var(--shell-inset-right)))",
          right: "calc(16px + var(--shell-inset-right))",
          top: "calc(var(--shell-top-nav-height-mobile) + 0.75rem)",
        }}
        offset={{
          right: "calc(24px + var(--shell-inset-right))",
          top: "calc(var(--shell-top-nav-height-desktop) + 0.75rem)",
        }}
        position="top-right"
      />
    </>
  );
}
